import { FileText, Image as ImageIcon, Loader2, Music2, Video } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { MediaPreviewFrame, MediaPreviewImage } from '../primitives/MediaPreview';
import type { DataExplorerMediaPreviewProps } from './types';
import { cn } from './utils';
import { safeDisplayUrl, safeDisplaySrcSet } from '../safeDisplayUrl';

function PreviewIcon({ kind }: { kind: NonNullable<DataExplorerMediaPreviewProps['kind']> }) {
  const Icon = kind === 'image' ? ImageIcon : kind === 'video' ? Video : kind === 'audio' ? Music2 : FileText;
  return <Icon className="h-6 w-6" aria-hidden="true" />;
}

export function DataExplorerMediaPreview({
  src,
  kind = 'image',
  alt = 'Preview',
  title,
  aspectRatio = 'auto',
  fit = 'contain',
  controls = true,
  loading = false,
  empty = 'No preview available',
  error = 'Preview is unavailable right now.',
  className,
  style,
  classNames,
  mediaAttributes,
  onLoad,
  onError,
}: DataExplorerMediaPreviewProps) {
  const [hasError, setHasError] = useState(false);
  const resolvedSrc = safeDisplayUrl(src, 'media') ?? '';
  const safeMediaAttributes = Object.fromEntries(Object.entries(mediaAttributes ?? {}).flatMap(([key, value]) => {
    const name = key.toLowerCase();
    if (name === 'src' || name === 'srcdoc' || name === 'dangerouslysetinnerhtml') return [];
    if (name === 'srcset') return [['srcSet', safeDisplaySrcSet(value)]];
    if (name === 'poster') return [['poster', safeDisplayUrl(value, 'media')]];
    return [[key, value]];
  }));

  useEffect(() => {
    setHasError(false);
  }, [resolvedSrc, kind]);

  const renderPlaceholder = (content: ReactNode, placeholderClassName?: string) => (
    <div
      className={cn(
        'flex min-h-24 flex-col items-center justify-center gap-2 bg-muted/30 p-4 text-center text-sm text-muted-foreground',
        classNames?.placeholder,
        placeholderClassName,
      )}
      role={loading ? 'status' : 'img'}
      aria-label={typeof content === 'string' ? content : undefined}
    >
      {content}
    </div>
  );

  if (loading) {
    return (
      <div className={cn('min-w-0 overflow-hidden', classNames?.root, className)} style={style} aria-busy="true">
        <div
          className={cn(
            'flex min-h-24 flex-col items-center justify-center gap-2 bg-muted/30 p-4 text-sm text-muted-foreground',
            classNames?.loading,
          )}
        >
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          {title || 'Loading preview...'}
        </div>
      </div>
    );
  }

  if (!resolvedSrc || hasError) {
    return (
      <div className={cn('min-w-0 overflow-hidden', classNames?.root, className)} style={style}>
        {renderPlaceholder(
          <>
            <PreviewIcon kind={kind} />
            <span className={cn('max-w-full truncate', hasError ? classNames?.error : undefined)}>
              {hasError ? error : empty}
            </span>
            {title ? <span className="max-w-full truncate text-xs">{title}</span> : null}
          </>,
        )}
      </div>
    );
  }

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const media =
    kind === 'image' ? (
      <MediaPreviewImage
        {...safeMediaAttributes}
        src={resolvedSrc}
        alt={alt}
        fit={fit}
        className={classNames?.media}
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={onLoad}
        onError={handleError}
      />
    ) : kind === 'video' ? (
      <video
        {...safeMediaAttributes}
        src={resolvedSrc}
        className={cn('block h-auto w-full', fit === 'cover' ? 'object-cover' : 'object-contain', classNames?.media)}
        controls={controls}
        preload="metadata"
        onLoadedData={onLoad}
        onError={handleError}
      />
    ) : kind === 'audio' ? (
      <audio
        {...safeMediaAttributes}
        src={resolvedSrc}
        className={cn('w-full', classNames?.media)}
        controls={controls}
        preload="metadata"
        onCanPlay={onLoad}
        onError={handleError}
      />
    ) : (
      renderPlaceholder(
        <>
          <PreviewIcon kind="file" />
          <span className="max-w-full truncate">{title || resolvedSrc}</span>
        </>,
      )
    );

  if (kind === 'image') {
    return (
      <MediaPreviewFrame aspectRatio={aspectRatio} className={cn(classNames?.root, className)} style={style}>
        {media}
      </MediaPreviewFrame>
    );
  }

  return <div className={cn('min-w-0 overflow-hidden', classNames?.root, className)} style={style}>{media}</div>;
}
